<template>
    <section id="collections" class="side-padding">
        <h1>{{ content.title }}</h1>
        <p>{{ content.lead_in }}</p>

        <ul class="collections img-links">
            <li v-for="t in collections" v-bind:key="t.title">
                <a :href="$router.resolve({ path: `${advRoute}?${t.search_query}` }).href">
                    <img :src="getImage(t.image)" :alt="t.title">
                    <div class="text">
                        <p class="h2">{{ t.title }}</p>
                        <p>{{ t.description }}</p>
                    </div>
                </a>
            </li>
        </ul>

        <JsonLD :data="json_ld"></JsonLD>
    </section>
</template>

<script>
import JsonLD from '../components/JsonLD'
import { mapGetters } from 'vuex'

export default {
    components: {
        JsonLD,
    },
    head() {
        return {
            title: function () {
                return {
                    inner: this.$i18n.t(`collections_page.${this.site}.title`),
                    separator: '|',
                    complement: this.$i18n.t(`main.${this.site}.museum_name`)
                }
            },
            meta: function () {
                return [
                    { rel: 'canonical', href: this.$i18n.t(`main.${this.site}.cannonical`), id: 'rel-con' },
                    { rel: 'alternate', href: this.$i18n.t(`main.${this.site}.alternate`), hreflang: this.$i18n.t(`main.${this.site}.alternate_locale`), id: 'rel-alt' },

                    { name: 'description', content: this.$i18n.t(`main.${this.site}.description`), id: 'desc' },

                    { property: 'og:title', content: `${this.$i18n.t(`main.${this.site}.site_name`)} | ${this.$i18n.t(`main.${this.site}.museum_name`)}`, id: 'og-title' },
                    { property: 'og:description', content: this.$i18n.t(`main.${this.site}.cannonical`), id: 'og-desc' },
                    { property: 'og:type', content: 'website', id: 'og-type' },
                    { property: 'og:locale', content: this.$i18n.locale, id: 'og-locale' },
                    { property: 'og:image', content: `${window.location.host}${this.$i18n.t(`main.${this.site}.preview_image`)}`, id: 'og-image' }
                ]
            }
        }
    },
    watch: {
        'site': function () {
            this.$emit('updateHead')
        }
    },
    computed: {
        ...mapGetters({
            'site': 'search/site'
        }),
        content() {
            return this.$i18n.messages[this.$i18n.locale].collections_page[this.site];
        },
        collections() {
            let collections = this.$i18n.messages[this.$i18n.locale].collections_page[this.site].collections;
            return collections;
        },
        json_ld() {
            let json = {
                "@type": "CollectionPage",
                "name": this.content.title,
                "url": window.location.href,
                "mainEntity": [{
                    "@type": "ItemList",
                    "name": this.content.title
                }]
            }
            let url_base = window.location.origin + `/${this.$i18n.messages[this.$i18n.locale].routes.advanced_search}/?`
            let items = this.collections.map((c, i) => {
                return {
                    "@type": "ListItem",
                    "position": i + 1,
                    "item": {
                        "@type": "WebPage",
                        "url": url_base + c.search_query,
                        "name": c.title
                    }
                }
            })

            json["mainEntity"][0].itemListElement = items;
            return json;
        },
        basicRoute() {
            return this.$i18n.messages[this.$i18n.locale].routes.basic_search;
        },
        advRoute() {
            return this.$i18n.messages[this.$i18n.locale].routes.advanced_search;
        }
    },
    methods: {
        getImage(path) {
            try {
                let img = require(`@/assets/${path}`);
                return img;
            } catch (e) {
                return '';
            }
        }
    }

}
</script>

<style scoped lang="scss">
#collections {
    text-align: center;
    margin-top: $s4;

    h1,
    p {
        max-width: $max-text-width;
        margin: $s2 auto;
    }

    .img-links {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        li {
            display: inline-block;
            position: relative;
            flex-basis: 49%;
            margin-top: 2rem;
            max-height: 470px;
            // overflow: hidden;

            &:after {
                content: '';
                background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.6) 100%);
                height: 100%;
                width: 100%;
                display: block;
                position: absolute;
                top: 0;
                left: 0;
            }

            img {
                object-fit: cover;
                height: 100%;
                width: 100%;
                min-height: 300px;
            }

            a {
                color: $white;
                text-decoration: none;
                display: block;
                height: 100%;

                &:focus {
                    outline-offset: 4px;
                }
            }

            .text {
                text-align: left;
                z-index: 1;
                padding: 1rem 2.275rem;
                position: absolute;
                bottom: 0;
                left: 0;
                color: $white;
                height: 100%;
                box-sizing: border-box;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                align-items: flex-start;
                transition: background 250ms;

                p {
                    margin-top: 0;
                    margin-bottom: 1rem;
                    max-width: unset;
                    margin-left: 0;

                    &.h2 {
                        margin-bottom: .5rem;
                    }
                }

                &:hover {
                    background: rgba(0, 0, 0, .4);
                }
            }
        }
    }

    @media (max-width: $mq-max-width-tablet) {
        .img-links {
            flex-direction: column;
            flex-wrap: nowrap;

            li {
                margin-top: $s1;
                flex-basis: 100%;
                height: 470px;

                a {
                    height: 100%;
                    width: 100%;
                }

                img {
                    object-position: center;
                }

                .text p {
                    line-height: 1.2;
                }
            }
        }
    }
}
</style>